import PropTypes from "prop-types";
import axios from "axios";
import {
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
  AxiosInstance
} from "axios";
import { useMemo, useContext } from "react";
import { createContext } from "react";
import { CognitoAuthContext } from "components/Authentication/CognitoAuthContext";
import { siteSettings } from "core/siteSettings";

interface AxiosContextInterface {
  axios: AxiosInstance;
  loading: boolean;
}

export const AxiosContext = createContext<AxiosContextInterface | null>(null);
AxiosContext.displayName = "AxiosContext";
/**
 * AxiosProvider sets up an axios client that can be reused
 * across the application. We need to do this because some
 * parameters must be set globally, and it is quite a lot of
 * hassle to set this up this in every hook, etc. that must use
 * axios for data fetching.
 *
 * The provider should be used within the AuthProvider context,
 * as without this, the getToken method will not work correctly.
 *
 * The provider itself has two values - 'axios', which is the client
 * instance (see axios.create() documentation for examples of how to use),
 * and 'loading'. The loading parameter should be checked against before
 * trying to use the client, as it may not be ready.
 *
 * @param {} children Child components that the provider wraps.
 * @returns {AxiosContext.Provider}
 */
export function AxiosProvider({
  children
}: React.PropsWithChildren<{}>): JSX.Element {
  const url = siteSettings.siteApiURL;

  // This creates an instance of Axios for accessing the AIHub backend.
  // We may need to create additional instances in the future that might
  // have different configuration settings.

  const { getToken: cognitoGetToken } = useContext(CognitoAuthContext);

  const instance = useMemo(() => {
    const config: AxiosRequestConfig = {
      baseURL: url || undefined
    };
    const instance = axios.create(config);

    // Intercept all requests on this Axios instance and attach
    // the Authorization token from Cognito.
    instance.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        const token = await cognitoGetToken();

        // Ensure config.headers is initialized as an empty object if undefined
        config.headers = config.headers || {};

        // Append your request headers with an authenticated token
        config.headers["Authorization"] = `Bearer ${token}`;

        return config;
      }
    );
    return instance;
  }, [cognitoGetToken, url]);

  return (
    <AxiosContext.Provider value={{ axios: instance, loading: !url }}>
      {children}
    </AxiosContext.Provider>
  );
}

AxiosProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ])
};
